import React from 'react';
import {
  GraduationCap,
  Award,
  TrendingUp,
  ShieldCheck,
  BarChart3,
  Briefcase,
  Building2,
  Video,
  Presentation,
  Users,
  Wrench
} from 'lucide-react';
import { Button } from './ui/button';

const TrainingServices = () => {
  const specializations = [
    {
      title: 'ITIL',
      description: 'Modernize service management and align IT delivery with business goals.',
      icon: Briefcase,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'CMMI',
      description: 'Elevate process maturity and drive predictable, high-quality performance.',
      icon: TrendingUp,
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: 'ISO',
      description: 'Gain expertise to audit management systems against international standards.',
      icon: ShieldCheck,
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Lean Six Sigma',
      description: 'Eliminate operational waste and boost efficiency using data-driven precision.',
      icon: BarChart3,
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'CISSP',
      description: 'Master cybersecurity program leadership from architecture to operations.',
      icon: GraduationCap,
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  const deliveryFormats = [
    {
      title: 'On-Site Corporate Training',
      description: 'We bring the expertise to your office, customized to your business environment.',
      icon: Building2,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200'
    },
    {
      title: 'Virtual Instructor-Led Training (VILT)',
      description: 'Real-time, interactive sessions for remote and distributed teams.',
      icon: Video,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    },
    {
      title: 'Executive Briefings',
      description: 'High-level sessions for leadership to understand ROI and strategic value.',
      icon: Presentation,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      border: 'border-indigo-200'
    }
  ];

  const whyAnts = [
    {
      title: 'Expert Instructors',
      description: 'Learn from certified professionals and industry experts.',
      icon: Award,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Practical Approach',
      description: 'Real-world case studies and skills you can use immediately.',
      icon: Wrench,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Proven Track Record',
      description: 'Serving 150+ happy clients across the United States since 2006.',
      icon: Users,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50'
    }
  ];

  const handleScroll = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="training-services" className="pt-12 sm:pt-16 md:pt-20 pb-20 sm:pb-24 md:pb-32 bg-gradient-to-b from-white via-blue-50 to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-semibold text-xs uppercase tracking-wider">
              <GraduationCap className="w-4 h-4" />
              Professional Training Services
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Training <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-600"> Services</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Empowering your team with the knowledge to lead, implement, and excel in global industry standards.
          </p>
        </div>

        {/* Value Proposition */}
        <div className="mb-16 sm:mb-20">
          <div className="bg-gradient-to-br from-blue-50 via-white to-emerald-50 rounded-2xl p-8 sm:p-12 shadow-lg border-2 border-blue-200/50">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                  Knowledge is the Foundation of Compliance
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  At ANTS Corp, we don’t just help you in the certification process; we ensure your team gains the skills to maintain excellence. Our training programs are led by world-class experts with decades of hands-on experience in ISO, CMMI, CISSP, and ITIL standards. Transform your workforce from “compliant” to “competitive.”
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Core Training Specializations */}
        <div className="mb-16 sm:mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            Core Training Specializations
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializations.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">
                    {item.title}
                  </h4>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Training Delivery Formats */}
        <div className="mb-16 sm:mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            Training Delivery Formats
          </h3>
          <p className="text-center text-slate-600 max-w-3xl mx-auto mb-10">
            We understand that every organization has different needs. We offer flexible delivery models:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliveryFormats.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`rounded-2xl p-6 border ${item.border} ${item.bg} shadow-sm`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4 ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">
                    {item.title}
                  </h4>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why ANTS Corp */}
        <div className="mb-16 sm:mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            Why ANTS Corp?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyAnts.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                >
                  <div className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center mb-4 ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">
                    {item.title}
                  </h4>
                  <p className="text-slate-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => handleScroll('#contact')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg"
          >
            Schedule Training Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrainingServices;